import { openDB, DBSchema, IDBPDatabase } from 'idb';
import { logger } from '@/utils/logger';

interface FileCache extends DBSchema {
    files: {
        key: string; // fileId
        value: {
            fileId: string;
            projectId: string;
            blob: Blob;
            mimeType: string;
            fileName: string;
            cachedAt: number;
        };
    };
}

let db: IDBPDatabase<FileCache> | null = null;

const DB_NAME = 'runsheet-file-cache';
const DB_VERSION = 1;

/**
 * Initialize the IndexedDB database
 */
async function getDB(): Promise<IDBPDatabase<FileCache>> {
    if (db) return db;

    db = await openDB<FileCache>(DB_NAME, DB_VERSION, {
        upgrade(database) {
            if (!database.objectStoreNames.contains('files')) {
                database.createObjectStore('files', { keyPath: 'fileId' });
            }
        },
    });

    return db;
}

/**
 * Cache a file for offline access
 */
export async function cacheFile(
    fileId: string,
    projectId: string,
    url: string,
    mimeType: string,
    fileName: string
): Promise<void> {
    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch file for caching');

        const blob = await response.blob();
        const database = await getDB();

        await database.put('files', {
            fileId,
            projectId,
            blob,
            mimeType,
            fileName,
            cachedAt: Date.now()
        });

        logger.files(`File ${fileId} cached successfully`);
    } catch (error: unknown) {
        logger.error('Failed to cache file:', error);
        throw error;
    }
}

/**
 * Retrieve a cached file
 */
export async function getCachedFile(fileId: string): Promise<Blob | null> {
    try {
        const database = await getDB();
        const cached = await database.get('files', fileId);
        return cached?.blob || null;
    } catch (error: unknown) {
        logger.error('Failed to get cached file:', error);
        return null;
    }
}

/**
 * Check if a file is cached
 */
export async function isFileCached(fileId: string): Promise<boolean> {
    try {
        const database = await getDB();
        const cached = await database.get('files', fileId);
        return !!cached;
    } catch (error: unknown) {
        logger.error('Failed to check cache status:', error);
        return false;
    }
}

/**
 * Remove a file from cache
 */
export async function removeCachedFile(fileId: string): Promise<void> {
    try {
        const database = await getDB();
        await database.delete('files', fileId);
    } catch (error: unknown) {
        logger.error('Failed to remove cached file:', error);
        throw error;
    }
}

/**
 * Get all cached files for a project
 */
export async function getCachedFilesForProject(projectId: string): Promise<string[]> {
    try {
        const database = await getDB();
        const allFiles = await database.getAll('files');
        return allFiles
            .filter(f => f.projectId === projectId)
            .map(f => f.fileId);
    } catch (error: unknown) {
        logger.error('Failed to get cached files for project:', error);
        return [];
    }
}

/**
 * Clear all cached files for a project
 */
export async function clearProjectCache(projectId: string): Promise<void> {
    try {
        const database = await getDB();
        const allFiles = await database.getAll('files');
        const projectFiles = allFiles.filter(f => f.projectId === projectId);

        for (const file of projectFiles) {
            await database.delete('files', file.fileId);
        }

        logger.files(`Cleared cache for project ${projectId}`);
    } catch (error: unknown) {
        logger.error('Failed to clear project cache:', error);
        throw error;
    }
}

/**
 * Get total cache size in bytes
 */
export async function getCacheSize(): Promise<number> {
    try {
        const database = await getDB();
        const allFiles = await database.getAll('files');
        return allFiles.reduce((total, file) => total + file.blob.size, 0);
    } catch (error: unknown) {
        logger.error('Failed to calculate cache size:', error);
        return 0;
    }
}














